import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/shared/auth/AuthContext";
import { resolveAppSettings } from "@/modules/sys/api/sysApi";

export const DEFAULT_CURRENCY_SETTING_KEY = "pur.currency";

/** Ký hiệu hiển thị mặc định cho nội tệ (không dùng mã ISO VND/VNĐ làm suffix). */
export const DEFAULT_CURRENCY_SYMBOL = "đ";

let activeDefaultCurrency = DEFAULT_CURRENCY_SYMBOL;

/** Map VND / VNĐ / ₫ / Đ → đ; giữ nguyên ký hiệu khác ($, USD…). */
export function normalizeCurrencySymbol(raw: string | null | undefined): string {
  const t = raw?.trim() ?? "";
  if (!t) return DEFAULT_CURRENCY_SYMBOL;
  const upper = t.toUpperCase();
  if (upper === "VND" || upper === "VNĐ" || upper === "Đ" || t === "₫") {
    return DEFAULT_CURRENCY_SYMBOL;
  }
  return t;
}

export function getDefaultCurrency(): string {
  return activeDefaultCurrency;
}

export function setDefaultCurrency(currency: string | null | undefined) {
  if (currency && currency.trim()) {
    activeDefaultCurrency = normalizeCurrencySymbol(currency);
  }
}

/**
 * Hook truy vấn và đồng bộ đơn vị tiền tệ mặc định được cài đặt trong GIÁ TRỊ MẶC ĐỊNH (`pur.currency`).
 * Tự động cập nhật `activeDefaultCurrency` và cache trong 5 phút.
 */
export function useDefaultCurrency(): string {
  const { http } = useAuth();
  const query = useQuery({
    queryKey: ["sys", "settings", "resolved", DEFAULT_CURRENCY_SETTING_KEY],
    queryFn: async () => {
      try {
        const rows = await resolveAppSettings(http, [DEFAULT_CURRENCY_SETTING_KEY]);
        const val = rows.find((r) => r.key === DEFAULT_CURRENCY_SETTING_KEY)?.value?.trim();
        return normalizeCurrencySymbol(val || DEFAULT_CURRENCY_SYMBOL);
      } catch {
        return activeDefaultCurrency;
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  const currency = normalizeCurrencySymbol(query.data || activeDefaultCurrency);

  useEffect(() => {
    if (query.data) {
      setDefaultCurrency(query.data);
    }
  }, [query.data]);

  return currency;
}

/**
 * Chuẩn hóa đơn vị tiền tệ hiển thị:
 * - Trống → đơn vị mặc định (`pur.currency`, đã map VND/VNĐ → đ).
 * - Mã ISO nội tệ ("VND", "VNĐ") → ký hiệu mặc định đ.
 * - Giá trị đã lưu trên chứng từ (đ, ₫, $, …) → giữ nguyên theo field `currency`.
 */
export function resolveDisplayCurrency(currency?: string | null): string {
  const def = getDefaultCurrency();
  if (!currency || !currency.trim()) return def;
  return normalizeCurrencySymbol(currency) === DEFAULT_CURRENCY_SYMBOL
    ? def
    : currency.trim();
}

/**
 * Định dạng tiền tệ theo mã tiền tệ truyền vào hoặc mã mặc định cấu hình trong hệ thống (`pur.currency`).
 * Tự động chuẩn hóa các biến thể nội tệ (VND, đ) về đơn vị cấu hình mặc định.
 * Hỗ trợ các ký hiệu như "đ", "₫", "$" hoặc mã tiền tệ như "VND", "USD".
 */
export function formatMoney(
  value: number | null | undefined,
  currency?: string | null,
): string {
  if (value == null || Number.isNaN(value)) return "—";
  const cur = resolveDisplayCurrency(currency);
  const formatted = value.toLocaleString("vi-VN", { maximumFractionDigits: 2 });
  // Ký hiệu ngắn (đ, $, ₫…): viết sát số. Mã ISO 3 chữ (USD, EUR): cách bằng khoảng trắng.
  if (/^[A-Za-z]{3}$/.test(cur)) {
    return `${formatted} ${cur}`;
  }
  return `${formatted}${cur}`;
}

export function formatPoMoney(
  value: number | null | undefined,
  currency?: string | null,
): string {
  return formatMoney(value, currency);
}
